import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import AddIcon from "@mui/icons-material/AddOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import EditIcon from "@mui/icons-material/DriveFileRenameOutlineOutlined";
import { EditDetailsDialog } from "@/components/common/edit-details-dialog";
import { PageContainer, PageHeader } from "@/components/layout/page";
import { DataTable, type Column } from "@/components/data/data-table";
import { WorkspaceTag } from "@/components/display/workspace-tag";
import { EmptyState } from "@/components/feedback/states";
import { useToast } from "@/components/feedback/toaster";
import { useConfirm } from "@/components/feedback/confirm-dialog";
import { Form } from "@/components/form/form";
import { FormTextField } from "@/components/form/fields";
import { workspacesApi, type workspaceEntrySchema } from "@/api/settings";

export const Route = createFileRoute("/_app/settings/workspaces")({
  component: WorkspacesSettings,
});

type Workspace = z.infer<typeof workspaceEntrySchema>;

const schema = z.object({
  name: z.string().min(2, "Give the workspace a name"),
  description: z.string().optional(),
});

function WorkspacesSettings() {
  const toast = useToast();
  const confirm = useConfirm();
  const { data, isLoading, error, refetch } = workspacesApi.useList();
  const m = workspacesApi.useMutations();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Workspace | null>(null);

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: { name: "", description: "" },
  });

  const columns: Column<Workspace>[] = [
    { id: "name", header: "Name", cell: (r) => <WorkspaceTag name={r.name} color={r.color} /> },
    { id: "desc", header: "Description", cell: (r) => r.description ?? "—" },
    {
      id: "actions",
      header: "",
      align: "right",
      cell: (r) => (
        <>
          <IconButton
            size="small"
            aria-label="Edit workspace"
            onClick={(e) => {
              e.stopPropagation();
              setEditing(r);
            }}
          >
            <EditIcon fontSize="inherit" />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            aria-label="Delete workspace"
            onClick={async (e) => {
              e.stopPropagation();
              if (
                await confirm({
                  title: `Delete workspace "${r.name}"?`,
                  description:
                    "Pipelines and connectors in this workspace are not deleted, but lose their grouping.",
                  destructive: true,
                })
              ) {
                await m.remove.mutateAsync(r.id);
                toast.success("Workspace deleted");
              }
            }}
          >
            <DeleteIcon fontSize="inherit" />
          </IconButton>
        </>
      ),
    },
  ];

  async function onCreate(values: z.infer<typeof schema>) {
    await m.create.mutateAsync(values);
    toast.success("Workspace created");
    setCreating(false);
    form.reset();
  }

  return (
    <PageContainer>
      <PageHeader
        title="Workspaces"
        description="Group pipelines, connectors and schedules for a team or environment."
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreating(true)}>
            New workspace
          </Button>
        }
      />
      <Card>
        <DataTable
          columns={columns}
          data={data}
          isLoading={isLoading}
          error={error}
          onRetry={() => refetch()}
          rowKey={(r) => r.id}
          empty={
            <EmptyState
              title="No workspaces"
              description="Create a workspace to organise resources by team or environment."
            />
          }
        />
      </Card>

      <Dialog open={creating} onClose={() => setCreating(false)} maxWidth="xs" fullWidth>
        <DialogTitle>New workspace</DialogTitle>
        <Form form={form} onSubmit={onCreate}>
          <DialogContent>
            <Stack spacing={2}>
              <FormTextField control={form.control} name="name" label="Name" required />
              <FormTextField
                control={form.control}
                name="description"
                label="Description"
                multiline
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCreating(false)}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={form.formState.isSubmitting}>
              Create
            </Button>
          </DialogActions>
        </Form>
      </Dialog>

      {editing ? (
        <EditDetailsDialog
          open
          onClose={() => setEditing(null)}
          noun="workspace"
          withColor
          initial={{
            name: editing.name,
            description: editing.description ?? "",
            color: editing.color ?? "#6366f1",
          }}
          onSave={(v) =>
            m.update.mutateAsync({
              id: editing.id,
              body: { name: v.name, description: v.description, color: v.color },
            })
          }
        />
      ) : null}
    </PageContainer>
  );
}
